import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { api, ApiError } from '../../api.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { TopBar } from '../../components/TopBar.tsx';
import { BottomNav } from '../../components/BottomNav.tsx';
import { formatDateRange, formatEuros, MISSION_STATUS_LABELS } from '../../format.ts';
import type { ExtraProfile, Mission } from '../../types.ts';
import type { Overview } from './business/types.ts';

export function ExtraDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ExtraProfile | null | 'loading'>('loading');
  const [myMissions, setMyMissions] = useState<Mission[]>([]);
  const [available, setAvailable] = useState<Mission[]>([]);
  const [bio, setBio] = useState('');
  const [creating, setCreating] = useState(false);
  const [attest, setAttest] = useState(false);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [proPercent, setProPercent] = useState<number | null>(null);

  async function loadAll() {
    try {
      const res = await api.get<{ extra: ExtraProfile }>('/extras/me');
      setProfile(res.extra);
      api.get<Overview>('/extra/overview').then(setOverview).catch(() => undefined);
      api.get<{ profile: { completeness: { percent: number } } }>('/extra/pro-profile').then((r) => setProPercent(r.profile.completeness.percent)).catch(() => undefined);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setProfile(null);
      else throw err;
    }
    const mine = await api.get<{ missions: Mission[] }>('/missions/mine');
    setMyMissions(mine.missions);
    const search = await api.get<{ missions: Mission[] }>('/missions/search');
    setAvailable(search.missions);
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function createProfile() {
    setCreating(true);
    try {
      await api.post('/extras/profile', { bio, attestMultiClient: attest });
      await loadAll();
    } finally {
      setCreating(false);
    }
  }

  if (profile === 'loading') {
    return (
      <div className="app-shell">
        <TopBar title="Mon Extra" logo />
        <div className="screen-content empty-state">Chargement…</div>
      </div>
    );
  }

  if (profile === null) {
    return (
      <div className="app-shell">
        <TopBar title="Bienvenue" logo />
        <div className="screen-content">
          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Complétez votre profil extra</div>
            <label className="field-label" htmlFor="bio">Courte présentation</label>
            <textarea
              id="bio"
              className="input"
              rows={4}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Serveur avec 3 ans d'expérience en restauration événementielle…"
            />
            <div style={{ height: 12 }} />
            <label style={{ display: 'flex', gap: 8, fontSize: 12.5, lineHeight: 1.6, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} style={{ marginTop: 4 }} />
              <span>
                Je déclare sur l'honneur exercer mon activité auprès de plus d'un client sur l'année (via Mon Extra ou en dehors), sans qu'aucun
                client ne représente plus de 50 % de mon chiffre d'affaires. Cette déclaration est renouvelée chaque année.
              </span>
            </label>
            <div style={{ height: 12 }} />
            <button className="btn btn-primary" onClick={createProfile} disabled={creating || !attest}>
              {creating ? 'Création…' : 'Créer mon profil'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <TopBar title={`Bonjour ${user?.firstName ?? ''}`} logo />
      <div className="screen-content">
        <div className="stat-row">
          <div className="card stat-card">
            <div className="stat-value">{profile.reputation_score ? profile.reputation_score.toFixed(1) : '—'}</div>
            <div className="stat-label">Note moyenne</div>
          </div>
          <div className="card stat-card">
            <div className="stat-value">{profile.missions_completed}</div>
            <div className="stat-label">Missions</div>
          </div>
        </div>

        {proPercent !== null && proPercent < 100 && (
          <Link to="/extra/profil-pro" className="card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>Complétez votre profil professionnel ({proPercent} %)</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Postes, logiciels, langues, équipement : c'est ce qui vous fait remonter dans les recherches et candidater sans blocage.</div>
          </Link>
        )}

        {overview && (!overview.attestation.valid || overview.dependence.status === 'EXCEEDED' || overview.dependence.status === 'BLOCKED' || overview.urssaf.toDeclare) && (
          <Link to="/extra/activite" className="card" style={{ textDecoration: 'none', color: 'inherit', display: 'block', borderColor: 'var(--warning)' }}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>À faire dans « Mon activité »</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {!overview.attestation.valid && <li>Renouveler votre attestation sur l'honneur : sans elle, vous ne pouvez pas candidater.</li>}
              {(overview.dependence.status === 'EXCEEDED' || overview.dependence.status === 'BLOCKED') && <li>Rééquilibrer votre clientèle : un client dépasse {overview.dependence.thresholdPct} % de votre chiffre d'affaires.</li>}
              {overview.urssaf.toDeclare && <li>Déclarer votre chiffre d'affaires ({overview.urssaf.toDeclare.label}) à l'URSSAF.</li>}
            </ul>
          </Link>
        )}

        <div>
          <div style={{ fontSize: 14, fontWeight: 800, padding: '0 2px 8px' }}>Mes missions</div>
          {myMissions.length === 0 && <div className="empty-state">Aucune candidature pour l'instant.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {myMissions.map((m) => (
              <Link key={m.id} to={`/extra/missions/${m.id}`} className="card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 14.5 }}>{m.title}</div>
                  <span className="badge badge-neutral">{MISSION_STATUS_LABELS[m.status] ?? m.status}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {formatDateRange(m.starts_at, m.ends_at)}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 14, fontWeight: 800, padding: '0 2px 8px' }}>Missions disponibles</div>
          {available.length === 0 && <div className="empty-state">Aucune mission publiée pour le moment.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {available.map((m) => (
              <Link key={m.id} to={`/extra/missions/${m.id}`} className="card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div style={{ fontWeight: 800, fontSize: 14.5 }}>{m.title}</div>
                {m.organization_recommended && <span className="badge badge-available" style={{ marginTop: 4 }}>Établissement recommandé</span>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, color: 'var(--text-secondary)', fontSize: 12.5 }}>
                  <MapPin size={13} /> {m.city}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{formatDateRange(m.starts_at, m.ends_at)}</span>
                  <span style={{ fontWeight: 800, fontSize: 13.5 }}>{formatEuros(m.hourly_rate_cents)}/h</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
